import React from 'react';
import * as PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import ReactAutolink from 'react-autolink';
import { Link } from 'react-router-dom';
import loadable from '@loadable/component';
import { GivingButton } from '../../components/giving-button';
import { connect } from '../../connect';
import { amplitudeLogEvent } from '../../helpers/amplitude-helper';
import { createItemOpenGraphMetadata, createOembedLink, createItemVideoMetaData } from '../../helpers/metadata-helper';
import { formatDuration } from '../../helpers/duration-helper';
import { getItemCover } from '../../utils/item-utils';
import logger from '../../utils/logger';
import baseUrls from '../../data/baseUrls';
import { ItemDetailsEpisodes } from './item-details-episodes';
import { PlusRibbon } from '../../components/plus-ribbon';
import { LoadingIndicator } from '../../components/loading-indicator';
import { NotAvailable } from '../../components/not-available';
import { PlayButton } from '../../components/play-button';
import { ProgressBar } from '../../components/progress-bar';
import { ShareButtons } from '../../components/share-buttons';
import { ReportProblem } from '../../components/report-problem';
import { getMembershipLabel } from '../../utils/membership';
import {
	createFollowGroupMembership,
	createJoinGroupMembership,
	getAdminGroupsIfNecessary,
	getGroupSettingsRecommended,
	getGroupSettingsRestricted,
	getItem,
	getWatchlist,
} from '../../actions';
import './dropdown.less';
import './index.less';
import { IAuthState } from '../../reducers/auth-reducers';
import { ISubscriptionState } from '../../reducers/subscription-reducers';
import { IKidsModeState } from '../../reducers/kids-mode-reducers';
import { IAdminState } from '../../reducers/admin-reducers';
import { IGroupMembershipState } from '../../reducers/group-membership-reducers';
import { IGroupSettingsRecommendedState } from '../../reducers/group-settings-recommended-reducers';
import { IGroupSettingsRestrictedState } from '../../reducers/group-settings-restricted-reducers';
import { IItemState } from '../../reducers/item-reducers';
import { IReduxProps } from '../../actions/action-types';
import { IWatchlistState } from '../../reducers/watchlist-reducers';
import { IFeatureFlagsState } from '../../reducers/feature-flags-reducers';
import { WatchlistButton } from '../../components/watchlist-button';
import { ItemRating } from '../../components/item-rating';

interface ILinkAttributes {
	rel: string;
	type?: string;
	as?: string;
	href: string;
}

const GroupSettings: any = loadable(() => import('./group-settings'));

function mapDispatchToProps(dispatch) {
	return { dispatch };
}

function mapStateToProps(
	{
		item,
		admin,
		auth,
		subscription,
		groupMembership,
		groupSettingsRecommended,
		groupSettingsRestricted,
		kidsMode,
		watchlist,
		featureFlags,
	},
	{
		match: {
			params: { itemId, resourceId, mediaId },
		},
	}
) {
	resourceId = resourceId ? resourceId.toUpperCase() : null;
	mediaId = mediaId ? mediaId.toUpperCase() : null;
	return {
		item,
		admin,
		auth,
		subscription,
		itemId,
		resourceId,
		mediaId,
		groupMembership,
		groupSettingsRecommended,
		groupSettingsRestricted,
		kidsMode,
		watchlist,
		featureFlags,
	};
}

interface ItemDetailsPageProps extends IReduxProps {
	auth: IAuthState;
	admin: IAdminState;
	groupMembership: IGroupMembershipState;
	groupSettingsRecommended: IGroupSettingsRecommendedState;
	groupSettingsRestricted: IGroupSettingsRestrictedState;
	item: IItemState;
	itemId?: string;
	location: URL;
	mediaId?: string;
	resourceId?: string;
	subscription: ISubscriptionState;
	kidsMode: IKidsModeState;
	watchlist: IWatchlistState;
	featureFlags: IFeatureFlagsState;
}

interface ItemDetailsPageState {
	userRating: number | null;
}

@connect(mapStateToProps, mapDispatchToProps)
export default class ItemDetailsPage extends React.Component<ItemDetailsPageProps, ItemDetailsPageState> {
	private retryGetItem: boolean = false;
	private isSsrDone: boolean = false;

	state: ItemDetailsPageState = {
		userRating: null,
	};

	static contextTypes = {
		router: PropTypes.object.isRequired,
	};

	static async activate(store, { params: { itemId, resourceId, mediaId }, staticContext }) {
		resourceId = resourceId ? resourceId.toUpperCase() : null;
		mediaId = mediaId ? mediaId.toUpperCase() : null;
		await Promise.all([
			store.dispatch(getItem({ itemId, resourceId, mediaId })).catch(logger.error),
			store.dispatch(getWatchlist()).catch(logger.error),
		]);

		if (staticContext) {
			const {
				item: { error },
			} = store.getState();
			const statusCode = error?.statusCode;
			if (statusCode === 404) {
				staticContext.statusCode = 404;
			} else if (statusCode) {
				staticContext.statusCode = 500;
			}
		}
	}

	componentDidMount() {
		const {
			item: { item },
			admin,
			dispatch,
		} = this.props;
		if (item) {
			amplitudeLogEvent('Item - Viewed', {
				'Item Id': item.id,
				Title: item.title || 'Not Set',
			});
			if (admin.isLoaded) {
				dispatch(getGroupSettingsRecommended(item.id, true)).catch(logger.error);
				dispatch(getGroupSettingsRestricted(item.id, true)).catch(logger.error);
			} else {
				dispatch(getAdminGroupsIfNecessary()).catch(logger.error);
			}
		}
		this.isSsrDone = true;
	}

	componentWillReceiveProps(nextProps: ItemDetailsPageProps) {
		const {
			auth,
			groupMembership,
			admin,
			item: { item },
			dispatch,
			itemId,
		} = this.props;
		const {
			auth: nextAuth,
			groupMembership: nextGroupMembership,
			admin: nextAdmin,
			item: { item: nextItem },
		} = nextProps;

		if (
			!nextAuth.isProcessing &&
			nextAuth.user &&
			nextAuth.user.id !== auth.user.id &&
			nextAuth.user.id !== '-1' &&
			!this.retryGetItem
		) {
			this.retryGetItem = true;
			this.reloadItem();
		}
		if (
			!nextGroupMembership.isProcessing &&
			nextGroupMembership.created !== groupMembership.created &&
			nextGroupMembership.created
		) {
			this.reloadItem();
		}
		if (nextItem && !item) {
			amplitudeLogEvent('Item - Viewed', {
				'Item Id': nextItem.id,
				Title: nextItem.title || 'Not Set',
			});
		}
		if (itemId && nextAdmin.isProcessing !== admin.isProcessing && nextAdmin.isLoaded) {
			dispatch(getGroupSettingsRecommended(itemId, true));
			dispatch(getGroupSettingsRestricted(itemId, true));
		}
	}

	private reloadItem() {
		const { dispatch, itemId, resourceId, mediaId } = this.props;
		return Promise.all([
			dispatch(getItem({ itemId, resourceId, mediaId }, { reload: true })),
			itemId && dispatch(getGroupSettingsRecommended(itemId, true)),
			itemId && dispatch(getGroupSettingsRestricted(itemId, true)),
		]);
	}

	private createGroupMembership(groupId: string, action: string) {
		const {
			dispatch,
			auth,
			item: { item },
		} = this.props;
		const isAuthenticated = !auth.isProcessing && auth.user && auth.user.id !== '-1';

		if (!isAuthenticated) {
			const returnUrl = item && item.id ? `/items/${item.id}` : '/';
			this.context.router.history.push(
				`/register?returnUrl=${encodeURIComponent(returnUrl)}&groupMembershipId=${encodeURIComponent(
					groupId
				)}&groupMembershipAction=${encodeURIComponent(action)}`
			);
		} else if (action === 'join') {
			dispatch(createJoinGroupMembership(groupId)).catch(logger.error);
		} else if (action === 'follow') {
			dispatch(createFollowGroupMembership(groupId)).catch(logger.error);
		}
	}

	private renderGroupInfo() {
		const {
			item: { item },
			groupMembership,
		} = this.props;
		const hasGroupMembershipError =
			groupMembership && !groupMembership.isProcessing && !groupMembership.created && groupMembership.error;
		const groupInfo = item && item.groupInfo;
		const membershipKind = groupInfo && groupInfo.membershipKind;
		const membershipKindText = getMembershipLabel(membershipKind);
		const faithlifeUrl = baseUrls.faithlife;
		return groupInfo ? (
			<div className="group-info">
				<div className="group-info-name">
					{groupInfo.token ? <a href={`${faithlifeUrl}/${groupInfo.token}`}>{groupInfo.name}</a> : groupInfo.name}
				</div>
				<div className="group-info-membership">
					{groupInfo.action && !hasGroupMembershipError ? (
						// TODO: Fix this the next time the file is edited.
						// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/anchor-is-valid, jsx-a11y/no-static-element-interactions
						<a onClick={() => this.createGroupMembership(groupInfo.groupId, groupInfo.action)}>
							{groupInfo.action === 'join' ? 'Join' : 'Follow'} on Faithlife
						</a>
					) : (
						membershipKindText && <span>{membershipKindText}</span>
					)}
				</div>
				{hasGroupMembershipError ? (
					<div className="b2" style={{ marginTop: 10 }}>
						Error Creating Group Membership
					</div>
				) : null}
			</div>
		) : (
			false
		);
	}

	private renderSoundFaith(soundFaithMetadata, subItems) {
		const faithlifeSermonsUrl = baseUrls.sermons;
		const isSeries = !!soundFaithMetadata.seriesId;
		const speakerAccount = soundFaithMetadata.speaker && soundFaithMetadata.speaker.speakerAccount;
		const speakerName = soundFaithMetadata.speaker && soundFaithMetadata.speaker.speakerName;
		const dateOptions = { year: 'numeric', month: 'long' };
		let datePresented;
		let sermonCount;
		let startDate;
		let endDate;
		const sermons = subItems ? subItems.filter(x => x.metadata && x.metadata.kind === 'soundfaith') : [];
		if (sermons.length) {
			sermonCount = sermons.length;
			const firstSermon = sermons[0];
			const lastSermon = sermons[sermons.length - 1];
			let firstDatePresented;
			let lastDatePresented;
			if (firstSermon.metadata && firstSermon.metadata.data && firstSermon.metadata.data.datePresented) {
				firstDatePresented = new Date(firstSermon.metadata.data.datePresented);
			}
			if (lastSermon.metadata && lastSermon.metadata.data && lastSermon.metadata.data.datePresented) {
				lastDatePresented = new Date(lastSermon.metadata.data.datePresented);
			}
			if (firstDatePresented) {
				if (
					lastDatePresented &&
					lastDatePresented.getMonth() !== firstDatePresented.getMonth() &&
					lastDatePresented.getFullYear() === firstDatePresented.getFullYear()
				) {
					startDate = firstDatePresented.toLocaleDateString(undefined, {
						month: 'long',
					});
				} else {
					startDate = firstDatePresented.toLocaleDateString(undefined, dateOptions);
				}
			}
			if (lastDatePresented) {
				endDate = lastDatePresented.toLocaleDateString(undefined, dateOptions);
			}
		}

		if (soundFaithMetadata.datePresented) {
			datePresented = new Date(soundFaithMetadata.datePresented);
		}

		return (
			<div className="soundfaith-container">
				{isSeries ? (
					<span style={{ marginRight: '10px' }}>
						{sermonCount ? `${sermonCount} sermon${sermonCount > 1 ? 's' : ''}` : false}
						{sermonCount && (startDate || endDate) ? ' \u2022 ' : false}
						{startDate ? `${startDate}` : false}
						{startDate && endDate && startDate !== endDate ? ` - ` : false}
						{endDate && startDate !== endDate ? `${endDate}` : false}
					</span>
				) : (
					<span style={{ marginRight: '10px' }}>
						{speakerAccount ? (
							<a href={`${faithlifeSermonsUrl}/profile/${speakerAccount.token}`}>{speakerAccount.name}</a>
						) : (
							false
						)}
						{speakerName || false}
						{datePresented && (speakerAccount || speakerName) ? ' \u2022 ' : false}
						{datePresented ? `Presented ${datePresented.toLocaleDateString()}` : false}
					</span>
				)}
				<a
					style={{ whiteSpace: 'nowrap' }}
					href={
						isSeries
							? `${faithlifeSermonsUrl}/series/${soundFaithMetadata.seriesId}`
							: `${faithlifeSermonsUrl}/sermons/${soundFaithMetadata.sermonId}`
					}
				>
					<img src="/public/images/FaithlifeSermons-Reversed.svg" alt="Faithlife Sermons" />
				</a>
			</div>
		);
	}

	render() {
		const {
			auth,
			subscription,
			itemId,
			groupMembership,
			groupSettingsRecommended,
			groupSettingsRestricted,
			location,
			kidsMode,
			item: { isFetching, item },
			watchlist: { itemIds: watchlistItemIds },
			location: { hash },
			dispatch,
		} = this.props;
		const faithlifeTvUrl = baseUrls.faithlifeTv;
		const isAuthenticated = !auth.isProcessing && auth.user && auth.user.id !== '-1';
		const isKidsMode = kidsMode && !!kidsMode.isKidsMode;
		const currentItemId = !item
			? undefined
			: item.currentSubItem
			? item.currentSubItem
			: item.subItems && item.subItems.length
			? item.subItems[0].id
			: item.id;
		const firstCover = item && item.covers && item.covers.length ? item.covers[0] : undefined;
		const orientation =
			firstCover && firstCover.height && firstCover.width && firstCover.height > firstCover.width
				? 'portrait'
				: 'landscape';
		const cover =
			orientation === 'portrait'
				? item && getItemCover({ item, width: 210, maxHeight: 400 })
				: item && getItemCover({ item, width: 400, maxHeight: 400 });
		const startTime =
			item && item.progress && item.progress.kind === 'time' && item.progress.value ? item.progress.value : 0;
		const itemDuration = item && item.duration;
		const duration = itemDuration
			? formatDuration(itemDuration)
			: item &&
			  item.subItems &&
			  item.subItems.length &&
			  item.subItems.some(si => si && si.metadata && si.metadata.kind === 'mobile-ed')
			? formatDuration(item.subItems.reduce((result, si) => (result += (si && si.duration) || 0), 0))
			: null;
		const progress = itemDuration && itemDuration > 0 ? (startTime / itemDuration) * 100 : 0;
		const progressBar = progress ? (
			<div className="description-progress-bar">
				<ProgressBar progress={progress} />
			</div>
		) : (
			false
		);

		const shareUrl = item && item.id ? `${faithlifeTvUrl}/items/${item.id}` : faithlifeTvUrl;
		const embedUrl = currentItemId ? `${faithlifeTvUrl}/media/${currentItemId}?embedded=1&autoplay=0` : undefined;
		const trailer = item && item.trailers && item.trailers.length ? item.trailers[0] : null;
		const userRating = this.state.userRating || (item && item.rating && item.rating.userRating) || undefined;
		const parentItem = item && item.parentItem;

		const meta = createItemOpenGraphMetadata({
			item,
			itemId: itemId,
		});
		const link: ILinkAttributes[] = [createOembedLink(`${faithlifeTvUrl}/items/${itemId}`)];
		if (cover?.url) {
			link.push({ rel: 'preload', as: 'image', href: cover.url });
		}
		const videoMeta = createItemVideoMetaData({
			item,
			itemId: itemId,
		});

		const hasGroupMembershipError =
			groupMembership && !groupMembership.isProcessing && !groupMembership.created && groupMembership.error;
		if (hasGroupMembershipError) {
			logger.error(groupMembership.error);
		}

		// prefer HLS stream
		const format =
			item && item.formats
				? [
						...item.formats.filter(x => x.type === 'application/x-mpegURL'),
						...item.formats.filter(x => x.type.startsWith('video/')),
				  ].shift()
				: null;

		const isLoading =
			isFetching || (!item && auth.isProcessing) || subscription.isFetching || groupMembership.isProcessing;
		const productLink =
			(item && item.productInfo && item.productInfo.addToCart) ||
			(item && item.productLink && { uri: item.productLink });

		const soundFaithMetadata = item && item.metadata && item.metadata.kind === 'soundfaith' ? item.metadata.data : null;

		const groupInfo = item && item.groupInfo;

		const canPlay = !!(
			item &&
			(format ||
				soundFaithMetadata ||
				(item.promotion === 'MOBILE-ED-TV-CHURCH' && isAuthenticated) ||
				(!item.promotion && !productLink && !item.productInfo && !(groupInfo && groupInfo.groupRestrictedLicense)))
		);

		const { recommended: groupRecommended } = groupSettingsRecommended;
		const { restricted: groupRestricted } = groupSettingsRestricted;
		const showGroupSettings =
			(!groupSettingsRecommended.isProcessing &&
				!groupSettingsRestricted.isProcessing &&
				!!groupRecommended?.recommendedGroups?.length) ||
			!!groupRestricted?.restrictedGroups?.length;
		const groupGivingToken = groupInfo && groupInfo.groupGivingToken;
		const shouldShowGiveLink = !!(groupInfo && groupInfo.hasGiving && groupGivingToken);

		const activeSection = hash ? decodeURIComponent(hash.substring(1)) : '';

		return (
			<div className="item-details-page">
				<Helmet title={item && item.title ? item.title : ''} meta={meta} link={link} />
				{isLoading ? (
					<LoadingIndicator />
				) : !item ? (
					<NotAvailable user={auth.user} location={location} />
				) : (
					<div>
						<div className="wrapper">
							<div className="details" itemType="http://schema.org/VideoObject" itemScope>
								{videoMeta?.contentUrl && <meta itemProp="contentUrl" content={videoMeta.contentUrl} />}
								{videoMeta?.embedUrl && <meta itemProp="embedUrl" content={videoMeta.embedUrl} />}
								{item ? (
									<span>
										<meta itemProp="uploadDate" content={item.createdDate} />
										<span itemProp="publisher" itemType="http://schema.org/Organization" itemScope>
											{videoMeta?.publisher?.name && <meta itemProp="name" content={videoMeta.publisher.name} />}
											<span itemProp="logo" itemType="http://schema.org/ImageObject" itemScope>
												{videoMeta?.publisher?.logo?.url && (
													<meta itemProp="url" content={videoMeta.publisher.logo.url} />
												)}
											</span>
										</span>
									</span>
								) : null}

								<div className={`${orientation}-cover`}>
									<div className="cover-container">
										{cover ? (
											<img
												src={cover.url}
												width={cover.width}
												height={cover.height}
												className={orientation}
												itemProp="thumbnailUrl"
												alt={item.title}
											/>
										) : (
											<img className={orientation} itemProp="thumbnailUrl" alt={item.title} />
										)}
										{canPlay && <PlayButton itemId={currentItemId} externalPlayUrl={item.externalPlayUrl} />}
									</div>
									{canPlay ? (
										false
									) : groupInfo && groupInfo.groupRestrictedLicense && !soundFaithMetadata ? (
										<div className="purchase-area">
											{hasGroupMembershipError ? (
												<div>
													<div className="b2" style={{ marginBottom: 10 }}>
														Error Creating Group Membership
													</div>
													<div className="b2" style={{ marginTop: 10 }}>
														{groupInfo.name}
													</div>
												</div>
											) : null}
											{item.eventInfo && !item.eventInfo.isOnline ? (
												<div>The event is not currently live.</div>
											) : (
												<div>
													{groupInfo.action === 'join' && !hasGroupMembershipError ? (
														<button
															className="subscribe-button"
															onClick={() => this.createGroupMembership(groupInfo.groupId, groupInfo.action)}
														>
															{'Join to watch'}
														</button>
													) : groupInfo.action === 'follow' && !hasGroupMembershipError ? (
														<button
															className="subscribe-button"
															onClick={() => this.createGroupMembership(groupInfo.groupId, groupInfo.action)}
														>
															{'Follow to watch'}
														</button>
													) : (
														<Link to="/">
															<button className="subscribe-button">Back to browse</button>
														</Link>
													)}
													<div className="b2" style={{ marginTop: 10 }}>
														{groupInfo.name}
													</div>
												</div>
											)}
										</div>
									) : item.promotion ? (
										<PlusRibbon />
									) : (
										false
									)}
								</div>
								<div className="description">
									{parentItem ? (
										<div className="description-parent-link">
											<Link to={`/items/${parentItem.id}`}>{parentItem.title}</Link>
										</div>
									) : (
										false
									)}
									<h2 itemProp="name">{item.title}</h2>
									{soundFaithMetadata ? this.renderSoundFaith(soundFaithMetadata, item.subItems) : false}
									{duration ? (
										<time
											itemProp="duration"
											className="duration"
											dateTime={videoMeta ? videoMeta.duration : undefined}
										>{`Duration: ${duration}`}</time>
									) : (
										false
									)}
									{progressBar}
									<ItemRating
										itemTitle={item.title}
										itemId={item.id}
										rating={item.rating}
										dispatch={dispatch}
										location={location}
										isAuthenticated={isAuthenticated}
										canRate={isAuthenticated && canPlay}
										onItemRated={userRating => this.setState({ userRating })}
									/>
									{itemId && isAuthenticated && !isKidsMode && (
										<WatchlistButton itemId={itemId} watchlistItemIds={watchlistItemIds} dispatch={dispatch} />
									)}
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<div>
											{trailer ? (
												<div className="trailer">
													<Link to={'/media/' + trailer.id}>Watch Trailer</Link>
												</div>
											) : (
												false
											)}
											{shouldShowGiveLink ? (
												<div className="group-info-actions">
													<GivingButton groupToken={groupGivingToken} />
												</div>
											) : null}
										</div>
										{item.groupInfo ? this.renderGroupInfo() : false}
									</div>
									<div itemProp="description" className="description-text">
										{ReactAutolink.autolink(item.description, {
											rel: 'nofollow',
										})}
									</div>
									{showGroupSettings && (
										<GroupSettings
											groupSettingsRecommended={groupSettingsRecommended}
											groupSettingsRestricted={groupSettingsRestricted}
											item={item}
											dispatch={dispatch}
										/>
									)}
									{canPlay && (
										<div>
											<ReportProblem itemId={item.id} itemTitle={item.title} />
										</div>
									)}
									{!isKidsMode && (
										<ShareButtons
											url={shareUrl}
											title={(item && item.title) || ''}
											userRating={userRating}
											embedUrl={embedUrl}
										/>
									)}
								</div>
								<div style={{ clear: 'both' }} />
							</div>
							{this.isSsrDone ? (
								<ItemDetailsEpisodes
									item={item}
									isAuthenticated={isAuthenticated}
									subscription={subscription}
									initialActiveSection={activeSection}
									kidsMode={kidsMode}
								/>
							) : (
								false
							)}
						</div>
					</div>
				)}
			</div>
		);
	}
}
